<h2 mat-dialog-title>{{dialogTitle}}</h2>

<mat-dialog-content class="dialog-content">
  <div *ngIf="loading" class="state-message">Loading linked accounts, teams, and templates...</div>
  <div *ngIf="!loading && loadError" class="state-message error">{{loadError}}</div>

  <form *ngIf="!loading && !loadError" class="dialog-grid" [formGroup]="form">
    <div class="section">
      <h3>Target</h3>
      <div class="row two-col">
        <mat-form-field appearance="outline">
          <mat-label>Linked Plaid Account</mat-label>
          <mat-select formControlName="targetAccountId">
            <mat-option *ngFor="let account of accounts" [value]="account.accountId">
              {{account.name}}<span *ngIf="account.mask"> •••{{account.mask}}</span><span *ngIf="account.institutionName"> - {{account.institutionName}}</span>
            </mat-option>
          </mat-select>
          <mat-error *ngIf="form.get('targetAccountId')?.touched && form.get('targetAccountId')?.invalid">
            Choose a linked account.
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Team Context</mat-label>
          <mat-select formControlName="teamId">
            <mat-option *ngFor="let team of teams" [value]="team.id">{{team.name}}</mat-option>
          </mat-select>
          <mat-error *ngIf="form.get('teamId')?.touched && form.get('teamId')?.invalid">
            Choose a team.
          </mat-error>
        </mat-form-field>
      </div>
    </div>

    <div class="section">
      <div>
        <h3>Add Transactions</h3>
        <div class="result-meta">Use bundles, custom transactions, or both. They all become the same tool-generated sandbox data after apply.</div>
      </div>

      <div class="composer-grid">
        <div class="template-card">
          <div class="template-header">
            <div>
              <div class="template-title">From Bundles</div>
              <div class="template-purpose">Pick one or more predefined reimbursement scenarios.</div>
            </div>
          </div>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Search bundles</mat-label>
            <input matInput [formControl]="templateSearchControl" placeholder="Search by label, purpose, or tag">
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Bundles</mat-label>
            <mat-select formControlName="templateIds" multiple>
              <mat-option *ngFor="let template of filteredTemplates" [value]="template.id">
                {{template.label}}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <div class="tag-list" *ngIf="selectedTemplates.length">
            <span class="tag" *ngFor="let template of selectedTemplates">{{template.label}}</span>
          </div>
        </div>

        <div class="template-card">
          <div class="results-header">
            <div>
              <div class="template-title">Custom Transactions</div>
              <div class="template-purpose">Add one-off Plaid-like transactions with the same sandbox lifecycle as bundles.</div>
            </div>
            <div class="results-actions">
              <button mat-stroked-button type="button" (click)="addCustomTransaction()">Add Transaction</button>
            </div>
          </div>

          <div *ngIf="!customTransactionControls.length" class="state-message inline-state-message">
            No custom transactions added yet.
          </div>

          <div formArrayName="customTransactions" class="custom-transaction-list" *ngIf="customTransactionControls.length">
            <div
              class="custom-transaction-card"
              *ngFor="let customTransaction of customTransactionControls; let index = index"
              [formGroupName]="index">
              <div class="custom-transaction-header">
                <div class="template-title">Custom {{index + 1}}</div>
                <button mat-icon-button type="button" (click)="removeCustomTransaction(index)" aria-label="Remove custom transaction">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>

              <div class="custom-transaction-fields">
                <div class="field-stack field-span-2">
                  <div class="field-name">Merchant</div>
                  <mat-form-field appearance="outline" class="full-width">
                    <input matInput formControlName="merchant" maxlength="45" placeholder="DoorDash Order">
                  </mat-form-field>
                </div>

                <div class="field-stack">
                  <div class="field-name">Reimbursement Category</div>
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-select formControlName="teamCategoryName">
                      <mat-option *ngFor="let category of categories" [value]="category.name">{{category.name}}</mat-option>
                    </mat-select>
                  </mat-form-field>
                </div>

                <div class="field-stack field-span-2">
                  <div class="override-row">
                    <mat-checkbox formControlName="useSeedCategoryOverride">Store different category</mat-checkbox>
                    <button
                      mat-icon-button
                      type="button"
                      class="info-button"
                      [matTooltip]="seedCategoryOverrideHelpText"
                      aria-label="Explain stored category override">
                      <mat-icon>info</mat-icon>
                    </button>
                  </div>
                  <div class="field-help" *ngIf="customTransaction.get('useSeedCategoryOverride')?.value">
                    The reimbursement category above remains the test intent. This override changes the stored transaction category written to the generated record.
                  </div>
                </div>

                <div class="field-stack" *ngIf="customTransaction.get('useSeedCategoryOverride')?.value">
                  <div class="field-name">Stored Transaction Category</div>
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-select formControlName="seedCategoryName">
                      <mat-option *ngFor="let category of categories" [value]="category.name">{{category.name}}</mat-option>
                    </mat-select>
                    <mat-error *ngIf="customTransaction.get('seedCategoryName')?.touched && customTransaction.get('seedCategoryName')?.invalid">
                      Choose the stored category to write.
                    </mat-error>
                  </mat-form-field>
                </div>

                <div class="field-stack">
                  <div class="field-name">Amount</div>
                  <mat-form-field appearance="outline" class="full-width">
                    <input matInput type="number" min="0.01" step="0.01" formControlName="amount">
                  </mat-form-field>
                </div>

                <div class="field-stack">
                  <div class="field-name">Days Ago</div>
                  <mat-form-field appearance="outline" class="full-width">
                    <input matInput type="number" min="0" step="1" formControlName="daysAgo">
                  </mat-form-field>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="state-message error" *ngIf="form.touched && form.hasError('scenarioSelectionRequired')">
        Select at least one bundle or add at least one custom transaction.
      </div>

      <mat-slide-toggle formControlName="wipeBeforeApply">
        Wipe tool-generated data on this account before applying
      </mat-slide-toggle>
    </div>

    <div class="section" *ngIf="previewRows.length">
      <h3>Transactions To Apply</h3>
      <div class="template-card">
        <table class="template-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Merchant</th>
              <th>Reimbursement Category</th>
              <th>Stored Category</th>
              <th>Amount</th>
              <th>Days Ago</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let transaction of previewRows">
              <td>{{transaction.sourceLabel}}</td>
              <td>{{transaction.merchant}}</td>
              <td>{{transaction.teamCategoryName}}</td>
              <td>{{transaction.seedCategoryName || transaction.teamCategoryName}}</td>
              <td>{{transaction.amount | currency}}</td>
              <td>{{transaction.daysAgo}}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="section">
      <div class="results-header">
        <div>
          <h3>Current Tool-Generated Data</h3>
          <div class="result-meta" *ngIf="currentAccountData?.latestScenarioRunId">
            Latest run: <code>{{currentAccountData?.latestScenarioRunId}}</code>
          </div>
        </div>
        <div class="results-actions">
          <button mat-stroked-button type="button" (click)="refreshScenarioRun()" [disabled]="!latestResult || applyInFlight">
            Refresh Latest Run
          </button>
          <button mat-stroked-button type="button" (click)="refreshCurrentAccountData()" [disabled]="currentDataLoading">
            Refresh Current Data
          </button>
        </div>
      </div>

      <div *ngIf="currentDataLoading" class="state-message">Loading current tool-generated data...</div>
      <div *ngIf="!currentDataLoading && currentAccountData && !currentAccountData.hasToolGeneratedData" class="state-message">
        No tool-generated transactions are currently attached to this account.
      </div>

      <div *ngIf="!currentDataLoading && currentAccountData?.hasToolGeneratedData">
        <div class="summary-grid">
          <div class="summary-card">
            <div class="summary-label">Scenario Runs</div>
            <div class="summary-value">{{currentAccountData?.totalScenarioRuns}}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Transactions</div>
            <div class="summary-value">{{currentAccountData?.totalTransactions}}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Pending</div>
            <div class="summary-value">{{currentAccountData?.statusCounts?.pending}}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Visible In Home</div>
            <div class="summary-value">{{currentAccountData?.statusCounts?.visibleInHomeReimbursements}}</div>
          </div>
        </div>

        <div class="issues" *ngIf="currentAccountData?.verification?.issues?.length">
          <h4>Current Verification Issues</h4>
          <div class="issue" *ngFor="let issue of currentAccountData?.verification?.issues">
            {{issue.name}} ({{issue.transactionId}}): expected {{issue.expectedBehavior}} in {{issue.expectedTeamCategoryName}}, actual category {{issue.actualTeamCategoryName || 'unknown'}}, visible={{issue.actualHomeVisibility}}
          </div>
        </div>

        <div class="current-run-card" *ngFor="let scenarioRun of currentAccountData?.scenarioRuns">
          <div class="template-header">
            <div>
              <div class="template-title">Run {{scenarioRun.scenarioRunId}}</div>
              <div class="template-purpose">
                {{scenarioRun.transactionCount}} transaction(s)
                <span *ngIf="scenarioRun.teamId"> • Team {{scenarioRun.teamId}}</span>
                <span *ngIf="scenarioRun.latestTransactionDate"> • Latest {{scenarioRun.latestTransactionDate | date: 'mediumDate'}}</span>
              </div>
            </div>
            <div class="tag-list">
              <span class="tag" *ngFor="let templateId of scenarioRun.templateIds">{{templateId}}</span>
            </div>
          </div>

          <div class="mini-summary">
            <span>Expected visible: {{scenarioRun.verification.expectedHomeVisibleCount}}</span>
            <span>Actual visible: {{scenarioRun.verification.actualHomeVisibleCount}}</span>
            <span [class.error-text]="scenarioRun.verification.failedCount > 0">Failed: {{scenarioRun.verification.failedCount}}</span>
          </div>

          <table class="result-table">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Pending</th>
                <th>Home Visible</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let transaction of scenarioRun.transactions">
                <td>{{transaction.name}}</td>
                <td>{{transaction.actualTeamCategoryName || transaction.verification.expectedTeamCategoryName}}</td>
                <td>{{transaction.amount | currency}}</td>
                <td>{{transaction.transactionDate | date: 'mediumDate'}}</td>
                <td>{{transaction.pending ? 'Yes' : 'No'}}</td>
                <td>{{transaction.visibleInHomeReimbursements ? 'Yes' : 'No'}}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="section" *ngIf="actionError">
      <div class="state-message error">{{actionError}}</div>
    </div>

    <div class="section" *ngIf="latestResult">
      <div class="results-header">
        <div>
          <h3>Latest Result</h3>
          <div class="result-meta">Run: <code>{{latestResult.scenarioRunId}}</code></div>
        </div>
        <div class="results-actions">
          <button mat-stroked-button type="button" (click)="refreshScenarioRun()" [disabled]="applyInFlight">
            Refresh
          </button>
          <button mat-stroked-button color="warn" type="button" (click)="wipeScenario('selected_run')" [disabled]="wipeInFlight">
            Wipe Selected Run
          </button>
          <button mat-stroked-button color="warn" type="button" (click)="wipeScenario('all_tool_generated_for_account')" [disabled]="wipeInFlight">
            Wipe All Tool Data
          </button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-label">Transactions</div>
          <div class="summary-value">{{latestResult.verification.totalTransactions}}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Expected Visible</div>
          <div class="summary-value">{{latestResult.verification.expectedHomeVisibleCount}}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Actual Visible</div>
          <div class="summary-value">{{latestResult.verification.actualHomeVisibleCount}}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Failed Checks</div>
          <div class="summary-value" [class.error-text]="latestResult.verification.failedCount > 0">
            {{latestResult.verification.failedCount}}
          </div>
        </div>
      </div>

      <div *ngIf="latestResult.verification.issues.length" class="issues">
        <h4>Verification Issues</h4>
        <div class="issue" *ngFor="let issue of latestResult.verification.issues">
          {{issue.name}} ({{issue.transactionId}}): expected {{issue.expectedBehavior}} in {{issue.expectedTeamCategoryName}}, actual category {{issue.actualTeamCategoryName || 'unknown'}}, visible={{issue.actualHomeVisibility}}
        </div>
      </div>

      <table class="result-table">
        <thead>
          <tr>
            <th>Merchant</th>
            <th>Category</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Pending</th>
            <th>Home Visible</th>
            <th>Check</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let transaction of latestResult.transactions">
            <td>{{transaction.name}}</td>
            <td>{{transaction.actualTeamCategoryName || transaction.verification.expectedTeamCategoryName}}</td>
            <td>{{transaction.amount | currency}}</td>
            <td>{{transaction.transactionDate | date: 'mediumDate'}}</td>
            <td>{{transaction.pending ? 'Yes' : 'No'}}</td>
            <td>{{transaction.visibleInHomeReimbursements ? 'Yes' : 'No'}}</td>
            <td [class.error-text]="!transaction.verification.passed">{{transaction.verification.passed ? 'Pass' : 'Fail'}}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="section" *ngIf="latestWipeResult">
      <h3>Latest Wipe</h3>
      <div class="result-meta">
        Mode: <code>{{latestWipeResult.wipeMode}}</code>
        <span *ngIf="latestWipeResult.scenarioRunId"> • Run: <code>{{latestWipeResult.scenarioRunId}}</code></span>
        • Deleted: {{latestWipeResult.deletedCount}}
      </div>
    </div>
  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="wipeScenario('last_run')" [disabled]="loading || wipeInFlight">
    Wipe Last Run
  </button>
  <button mat-stroked-button type="button" (click)="close()">Close</button>
  <button mat-flat-button color="primary" type="button" (click)="applyScenario()" [disabled]="loading || applyInFlight">
    Apply Scenario
  </button>
</mat-dialog-actions>
